'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useCommerceSource,
  tx,
  type CollectionPoint,
} from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import { addressLine, directionsUrl, isOpenNow, nowIn } from './collectionHours';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Our stores: where to collect an online order, when each is open, and how to get there.
 *
 * <p>The list is the shop's collection points — the same list checkout offers — so a store on this
 * page is always one a shopper can actually choose. Hours are shown in the store's own time zone,
 * and "open now" is worked out there too.
 */
export interface StoreLocatorProps {
  title?: Localized;
  intro?: Localized;
}

const WEEK = [1, 2, 3, 4, 5, 6, 7];

export function StoreLocator({ title, intro }: StoreLocatorProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t, i18n } = useTranslation('site-builder-blocks');
  const [points, setPoints] = useState<CollectionPoint[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!commerce?.collectionPoints) return;
    commerce
      .collectionPoints()
      .then((offered) => {
        if (!cancelled) setPoints(offered ?? []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (!commerce?.collectionPoints) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="StoreLocator"
            adapterName="CommerceSource"
            reason={t('commerce.stores.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  const locale = i18n.language || 'en';
  const dayName = (weekday: number) => {
    // 2024-01-01 was a Monday; weekday names come from the browser's own locale data.
    const date = new Date(Date.UTC(2024, 0, weekday));
    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
    } catch {
      return String(weekday);
    }
  };
  const dateLabel = (iso: string) => {
    const [year = 1970, month = 1, day = 1] = iso.split('-').map(Number);
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
        new Date(Date.UTC(year, month - 1, day)),
      );
    } catch {
      return iso;
    }
  };

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 1040 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.stores.title')}
        </h1>
        <p {...part('intro')} style={introStyle}>
          {tx(intro) || t('commerce.stores.intro')}
        </p>

        {failed ? (
          <p role="alert" style={noticeStyle}>
            {t('commerce.stores.unavailable')}
          </p>
        ) : points === null ? null : points.length === 0 ? (
          <p style={noticeStyle}>{t('commerce.stores.none')}</p>
        ) : (
          <ul style={gridStyle}>
            {points.map((point) => {
              const open = isOpenNow(point);
              const today = nowIn(point.timeZone).weekday;
              return (
                <li key={point.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <h2 style={nameStyle}>{point.name}</h2>
                    <span style={open ? openBadgeStyle : closedBadgeStyle}>
                      {open ? t('commerce.stores.openNow') : t('commerce.stores.closedNow')}
                    </span>
                  </div>
                  <address style={addressStyle}>{addressLine(point)}</address>
                  {point.note && <p style={mutedStyle}>{point.note}</p>}

                  <h3 style={subheadingStyle}>{t('commerce.stores.hours')}</h3>
                  <dl style={hoursStyle}>
                    {WEEK.map((weekday) => {
                      const ranges = point.hours
                        .filter((row) => row.weekday === weekday)
                        .map((row) => `${row.opensAt}–${row.closesAt}`)
                        .join(', ');
                      return (
                        <div
                          key={weekday}
                          style={{ ...hoursRowStyle, fontWeight: weekday === today ? 600 : 400 }}
                        >
                          <dt style={{ margin: 0 }}>{dayName(weekday)}</dt>
                          <dd style={{ margin: 0 }}>{ranges || t('commerce.stores.closed')}</dd>
                        </div>
                      );
                    })}
                  </dl>

                  {point.closures.length > 0 && (
                    <p style={mutedStyle}>
                      {t('commerce.stores.closures')}{' '}
                      {point.closures
                        .map((closure) =>
                          closure.reason ? `${dateLabel(closure.date)} (${closure.reason})` : dateLabel(closure.date),
                        )
                        .join(', ')}
                    </p>
                  )}

                  <div style={actionsStyle}>
                    <a href={directionsUrl(point)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                      {t('commerce.stores.directions')}
                    </a>
                    {point.address.phone && (
                      <a href={`tel:${point.address.phone.replace(/\s+/g, '')}`} style={linkStyle}>
                        {t('commerce.stores.call', { phone: point.address.phone })}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </BlockRoot>
  );
}

const titleStyle: CSSProperties = { margin: '0 0 8px', fontSize: 'var(--font-size-3xl, 30px)' };
const introStyle: CSSProperties = { margin: '0 0 24px', color: 'var(--color-text-secondary, #6b7280)' };
const noticeStyle: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f9fafb)',
};
const gridStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
};
const cardStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  alignContent: 'start',
  padding: 20,
  borderRadius: 'var(--radius-card, 12px)',
  border: '1px solid var(--color-border, #e5e7eb)',
};
const nameStyle: CSSProperties = { margin: 0, fontSize: 20, fontWeight: 600 };
const badgeBase: CSSProperties = {
  alignSelf: 'start',
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
};
const openBadgeStyle: CSSProperties = {
  ...badgeBase,
  background: 'var(--color-success-50, #f0fdf4)',
  color: 'var(--color-success-700, #15803d)',
};
const closedBadgeStyle: CSSProperties = {
  ...badgeBase,
  background: 'var(--color-surface-muted, #f3f4f6)',
  color: 'var(--color-text-secondary, #6b7280)',
};
const addressStyle: CSSProperties = { fontStyle: 'normal' };
const mutedStyle: CSSProperties = { margin: 0, fontSize: 14, color: 'var(--color-text-secondary, #6b7280)' };
const subheadingStyle: CSSProperties = { margin: '8px 0 0', fontSize: 15, fontWeight: 600 };
const hoursStyle: CSSProperties = { margin: 0, display: 'grid', gap: 2, fontSize: 14 };
const hoursRowStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16 };
const actionsStyle: CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 };
const linkStyle: CSSProperties = { color: 'var(--color-primary, #2563eb)', fontWeight: 600 };
